import styled from 'styled-components'
import { cover, timingFunctions } from 'polished'

type LazyImageProps = {
  'data-src'?: string
  'data-srcset'?: string
  cover?: boolean
  transformRule?: string
  zIndex?: Number
  rounded?: boolean
  height?: number
  width?: number
  preload?: boolean
  style?: React.CSSProperties
}

/**
 * Helper for setting `data-sizes=auto` on the <img>, if a prop `data-srcset` is present.
 *
 * The `data-sizes=auto` property allows the lazysizes library to automatically handle selecting the right src from a srcset:
 * https://github.com/aFarkas/lazysizes#automatically-setting-the-sizes-attribute
 */
const automaticallyAddSizesAttributeWithSrcSet = (props: LazyImageProps) => {
  return {
    ...props,
    ...(props?.['data-srcset'] ? { 'data-sizes': 'auto' } : {}),
  }
}

const LazyImage = styled.img
  .attrs<LazyImageProps>(({ className = '', preload, ...props }) => ({
    className: className + ' lazyload' + (preload ? ' lazypreload' : ''),
    ...automaticallyAddSizesAttributeWithSrcSet(props),
  }))
  .withConfig({
    shouldForwardProp: (prop) => {
      return (
        ['cover', 'transformRule', 'zIndex', 'rounded', 'preload'].includes(
          prop
        ) !== true
      )
    },
  })<LazyImageProps>`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: ${({ height }) => (height ? `${height}px` : '100%')};
  object-fit: cover;

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  ${({ transformRule }) =>
    transformRule &&
    `
    transform: ${transformRule};
  `}

  ${({ zIndex }) =>
    zIndex &&
    `
    z-index: ${zIndex};
  `}

  opacity: 0;
  transition: opacity 300ms ease-out;
  transition-timing-function: ${timingFunctions('easeOutQuad')};

  &.lazyloaded {
    opacity: 1;
  }

  &.lazyload {
    opacity: 0;
  }

  ${(props) => props.cover && cover()}
`

export default LazyImage
